import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD, type MartinusAbacPayload } from '../permissions/abac-payloads/MartinusAbacPayload.js';
import { hasRoles } from '../permissions/api/hasRoles.js';
import { Role } from '../permissions/roles.js';
import type { UserWithPermissionFields } from '../permissions/UserWithPermissionFields.js';

export enum UserActions {
  /**
   * Can the user impersonate another user
   */
  IMPERSONATE = 'IMPERSONATE_USER',

  /**
   * Create users
   */
  CREATE_USER = 'CREATE_USER',

  /**
   * Can the user remove other users
   */
  REMOVE_USER = 'REMOVE_USER',
}
export interface UserOnUserPayload extends MartinusAbacPayload {
  /**
   * Extends the default payload with the user that is being viewed
   */
  viewedUser: Maybe<UserWithPermissionFields>;
}

const UserPolicyFunctions: Record<UserActions, LoggedInEvaluationFunction<UserOnUserPayload>> = {
  /**
   * Only Admins can impersonate and only other people.
   */
  [UserActions.IMPERSONATE]: async () => {
    return { allowed: false, reason: 'Nur Admins dürfen die Impersonation ausführen.' };
  },

  /**
   * TODO we never talked about this -> For now deny
   */
  [UserActions.REMOVE_USER]: async () => {
    return { allowed: false, reason: 'Nur Admins dürfen Nutzer löschen.' };
  },

  [UserActions.CREATE_USER]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.SCHULLEITER, Role.PSB, Role.SEKRETAER],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Recht zur Nutzererstellung.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Recht zur Nutzererstellung.' };
  },
};

export const UserPolicy: IPolicy<UserOnUserPayload> = {
  name: 'UserPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await UserPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
